import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('littlelearners-token'));
  const [isImpersonating, setIsImpersonating] = useState(() => localStorage.getItem('impersonating') === 'true');
  const [originalAdminToken, setOriginalAdminToken] = useState(() => localStorage.getItem('original-admin-token'));

  const api = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  // Update axios headers when token changes
  useEffect(() => {
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.Authorization;
    }
  }, [token, api]);

  const fetchUser = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    // If the account has 2FA enabled, the server returns a challenge instead of
    // an access token. The caller (LoginPage) inspects this and prompts for a
    // code, then calls verifyTotpLogin() to complete.
    if (response.data?.needs_totp) {
      return { needsTotp: true, challenge: response.data.otp_challenge_token };
    }
    const { access_token, user: userData } = response.data;
    localStorage.setItem('littlelearners-token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const verifyTotpLogin = async (challenge, code) => {
    const response = await api.post('/auth/login/verify-totp', { challenge, code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('littlelearners-token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await api.post('/auth/register', { name, email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('littlelearners-token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const studentLogin = async (studentCode) => {
    const response = await api.post('/auth/student-login', { student_code: studentCode });
    const { access_token, user: userData, child_id, child_name } = response.data;
    
    localStorage.setItem('littlelearners-token', access_token);
    localStorage.setItem('student-child-id', child_id);
    setToken(access_token);
    setUser({ ...userData, isStudent: true, studentChildId: child_id });
    
    return { user: userData, child_id, child_name };
  };

  const logout = () => {
    localStorage.removeItem('littlelearners-token');
    localStorage.removeItem('student-child-id');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('original-admin-token');
    setToken(null);
    setUser(null);
    setIsImpersonating(false);
    setOriginalAdminToken(null);
  };

  // Impersonate a user (admin only)
  const impersonateUser = async (userId, isOrgAdmin = false) => {
    // Save current admin token before impersonating
    localStorage.setItem('original-admin-token', token);
    setOriginalAdminToken(token);
    
    const endpoint = isOrgAdmin ? `/org-admin/impersonate/${userId}` : `/admin/impersonate/${userId}`;
    const response = await api.post(endpoint);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('littlelearners-token', access_token);
    localStorage.setItem('impersonating', 'true');
    setToken(access_token);
    setUser(userData);
    setIsImpersonating(true);
    
    return userData;
  };

  // Exit impersonation and return to admin account
  const exitImpersonation = async () => {
    if (!originalAdminToken) return;
    
    localStorage.setItem('littlelearners-token', originalAdminToken);
    localStorage.removeItem('impersonating');
    localStorage.removeItem('original-admin-token');
    setToken(originalAdminToken);
    setIsImpersonating(false);
    setOriginalAdminToken(null);
    
    // Fetch the admin user data
    try {
      const tempApi = axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${originalAdminToken}` }
      });
      const response = await tempApi.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to restore admin session:', error);
      logout();
    }
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      verifyTotpLogin,
      register,
      studentLogin,
      logout,
      updateUser,
      impersonateUser,
      exitImpersonation,
      isAuthenticated: !!user,
      isAdmin: user?.is_admin || false,
      isStudent: user?.isStudent || false,
      isImpersonating,
      api
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
